import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSetRecoilState } from 'recoil';

import { requestApi } from 'api/Api';

import { formatDateAndTimeZ, ia } from 'lib/helpers/utility';
import { usePatientUpcomingAppointments } from 'lib/hooks/queries/patients/usePatient';
import { usePatientForms } from 'lib/hooks/queries/patients/usePatientForms';
import { useUserDashboard } from 'lib/hooks/queries/user/useUserDashboard';

import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Modal from 'components/shared/Modal/Modal';

import { patientAppointment } from '../patientState';

import Forms from './components/Forms';
import LatestResults from './components/LatestResults';
import Messages from './components/Messages';
import UpcomingAppointments from './components/UpcomingAppointments';

const Dashboard = () => {
  const [latest_messages, setLatestMessage] = useState({
    subject: '',
    sender_name: '',
    date: new Date(),
    unread_messages: ''
  });
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const setAppointmentState = useSetRecoilState(patientAppointment);
  const latestResults = {
    result_name: '',
    practice_name: '',
    date_published: new Date()
  };
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();

  useUserDashboard({
    options: {
      onSuccess: ({ redirect, latest_messages, code }) => {
        if (code === 0) {
          setLatestMessage(latest_messages);
        } else {
          navigate(redirect);
        }
      }
    }
  });
  const { data: upcomingAppointmentsData } = usePatientUpcomingAppointments({
    options: {
      onSuccess: ({ redirect, appointments, code }) => {
        if (code === 0 && ia(appointments)) {
          setUpcomingAppointments(appointments);
          if (queryParams.get('modal') === 'true') {
            setIsOpen(true);
          }
        } else {
          navigate(redirect);
        }
      }
    }
  });
  const closestAppointment = upcomingAppointmentsData?.appointments[0];
  const { data: patientFormsRes } = usePatientForms({ params: { uncompleted: true } });
  const incompleteForms = patientFormsRes?.forms?.length || 0;

  const checkIn = async (appointmentId) => {
    try {
      setAppointmentState({ id: appointmentId });
      localStorage.setItem('appointmentId', appointmentId);
      const data = await requestApi({
        url: `/api/patient/checkin/start`,
        params: { appointmentId },
        navigate
      });
      if (data?.redirectTo) {
        navigate(data.redirectTo);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (upcomingAppointmentsData?.appointments) {
      setUpcomingAppointments(upcomingAppointmentsData.appointments);
    }
  }, [upcomingAppointmentsData]);

  return (
    <>
      <Modal
        handleOpen={isOpen && closestAppointment}
        handleClose={() => setIsOpen(false)}
        title="Upcoming Appointment"
        footer={
          <div className="flex w-full justify-between">
            <Button
              outlined
              text="Remind me later"
              color="neutral"
              onClick={() => setIsOpen(false)}
            />
            <Button
              text="Complete"
              onClick={() => checkIn(closestAppointment?.id)}
              data-qa="complete-btn"
            />
          </div>
        }>
        <div className="flex flex-col gap-1 p-2 py-4">
          <p>You have paperwork for an upcoming appointment.</p>
          <p className="pb-4"> Please complete now. </p>
          <p>Practice: {closestAppointment?.practice.name}</p>
          <p>
            {`Date & Time: ${formatDateAndTimeZ(
              closestAppointment?.starts_at,
              closestAppointment?.practice?.timezone
            )}`}
          </p>
          <p>
            Location:{' '}
            {closestAppointment?.location === 'virtual'
              ? closestAppointment?.virtual_link || 'Virtual'
              : closestAppointment?.practice.practiceAddress?.fullAddress}
          </p>
        </div>
      </Modal>
      <div className="grid grid-cols-3 gap-4 !p-4">
        <UpcomingAppointments
          title="Upcoming Appointments"
          upcomingAppointments={upcomingAppointments}
        />
        <Messages title="Messages" latestMessages={latest_messages} />
        <LatestResults title="Latest Results" latestResults={latestResults} />
        <Forms title="Incomplete Forms" incompleteForms={incompleteForms} />
      </div>
    </>
  );
};

export default withErrorBoundary(Dashboard);
